// Sound, vibration and a screen-edge flash after each scan, so operators know the
// result without looking at the screen.
//
//   scanFeedback('success' | 'error' | 'warning' | 'duplicate' | 'lineComplete' | 'orderComplete')
//
// Picking: 'success' = one unit picked, 'lineComplete' = that scan finished its line,
// 'orderComplete' = that scan finished the last line of the order.
//
// The flash is a window CustomEvent (SCAN_FLASH_EVENT) that <ScanFlash/> (mounted once
// in App) turns into a coloured glow. Sound can be muted per device; the setting is
// kept in localStorage under `scanSoundEnabled`.

export const SCAN_FLASH_EVENT = 'scanfeedback:flash';
export const SCAN_SOUND_CHANGED_EVENT = 'scanfeedback:soundchange';
const SOUND_STORAGE_KEY = 'scanSoundEnabled';

// Each tone is { freq (Hz), ms, gapMs (silence after it) }. Square waves cut through
// warehouse noise better than sine waves.
const PATTERNS = {
  success: {
    tones: [{ freq: 1760, ms: 80, gapMs: 0 }],
    wave: 'square',
    vibrate: 30,
    flash: 'success',
  },
  error: {
    tones: [{ freq: 180, ms: 140, gapMs: 70 }, { freq: 180, ms: 140, gapMs: 0 }],
    wave: 'sawtooth',
    vibrate: [80, 60, 80],
    flash: 'error',
  },
  warning: {
    tones: [{ freq: 660, ms: 90, gapMs: 40 }, { freq: 880, ms: 90, gapMs: 0 }],
    wave: 'square',
    vibrate: [40, 40, 40],
    flash: 'warning',
  },
  // Two rising notes: this line is done
  lineComplete: {
    tones: [{ freq: 1319, ms: 90, gapMs: 30 }, { freq: 1760, ms: 140, gapMs: 0 }],
    wave: 'square',
    vibrate: [40, 40, 80],
    flash: 'success',
  },
  // Rising four-note fanfare: every line on the order is picked
  orderComplete: {
    tones: [
      { freq: 1047, ms: 110, gapMs: 30 },
      { freq: 1319, ms: 110, gapMs: 30 },
      { freq: 1568, ms: 110, gapMs: 30 },
      { freq: 2093, ms: 380, gapMs: 0 },
    ],
    wave: 'square',
    vibrate: [100, 60, 100, 60, 300],
    flash: 'success',
  },
  duplicate: {
    tones: [{ freq: 880, ms: 90, gapMs: 40 }, { freq: 660, ms: 90, gapMs: 0 }],
    wave: 'square',
    vibrate: [40, 40, 40],
    flash: 'warning',
  },
};

// The feedback pattern for a kind; unknown kinds fall back to 'error' so a typo
// is never mistaken for success.
export const getFeedbackPattern = (kind) => PATTERNS[kind] || PATTERNS.error;

// Start offsets (ms) of each tone in a pattern
export const toneSchedule = (tones) => {
  let at = 0;
  return tones.map((tone) => {
    const start = at;
    at += tone.ms + tone.gapMs;
    return { ...tone, start };
  });
};

const readStorage = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

export const getScanSoundEnabled = () => readStorage(SOUND_STORAGE_KEY) !== 'false';

export const setScanSoundEnabled = (enabled) => {
  try {
    window.localStorage.setItem(SOUND_STORAGE_KEY, enabled ? 'true' : 'false');
  } catch {
    // Private mode / storage blocked: the toggle still works for this page load
  }
  window.dispatchEvent(new CustomEvent(SCAN_SOUND_CHANGED_EVENT, { detail: { enabled } }));
};

// ----- Audio -----

let audioContext = null;

const getAudioContext = () => {
  if (audioContext) return audioContext;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  try {
    audioContext = new Ctor();
  } catch {
    audioContext = null;
  }
  return audioContext;
};

let unlockInstalled = false;

// Browsers keep audio suspended until a user gesture. Resume the context on the first
// tap/click/key so the first scan beep isn't silent. Safe to call more than once.
export const installAudioUnlock = () => {
  if (unlockInstalled || typeof window === 'undefined') return;
  unlockInstalled = true;
  const unlock = () => {
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
    window.removeEventListener('pointerdown', unlock, true);
    window.removeEventListener('keydown', unlock, true);
  };
  window.addEventListener('pointerdown', unlock, true);
  window.addEventListener('keydown', unlock, true);
};

const playTones = ({ tones, wave }) => {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  const now = ctx.currentTime;
  for (const tone of toneSchedule(tones)) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const start = now + tone.start / 1000;
    const end = start + tone.ms / 1000;
    osc.type = wave;
    osc.frequency.setValueAtTime(tone.freq, start);
    // Short ramps avoid clicks at the start and end of each tone
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.2, start + 0.005);
    gain.gain.setValueAtTime(0.2, end - 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(end + 0.01);
  }
};

const scanFeedback = (kind) => {
  const pattern = getFeedbackPattern(kind);

  if (getScanSoundEnabled()) {
    try {
      playTones(pattern);
    } catch {
      // Audio is a nicety; never let it break a scan
    }
  }

  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try {
      navigator.vibrate(pattern.vibrate);
    } catch {
      // Some browsers throw when vibration is blocked
    }
  }

  window.dispatchEvent(new CustomEvent(SCAN_FLASH_EVENT, { detail: { kind: pattern.flash } }));
};

export default scanFeedback;
